export const HOME_SCREEN_PROMPT_DISMISSED_KEY = "join_install_prompt_dismissed";

type StandaloneNavigator = Navigator & {
  standalone?: boolean;
};

export type HomeScreenPromptEnvironment = {
  userAgent: string;
  platform: string;
  standalone: boolean;
  dismissed: boolean;
};

export function isIosSafari(userAgent: string, platform: string): boolean {
  const normalizedAgent = userAgent.toLowerCase();
  const normalizedPlatform = platform.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(normalizedAgent) || /iphone|ipad|ipod/.test(normalizedPlatform);
  const isSafari = normalizedAgent.includes("safari");
  const isAlternateIosBrowser = /(crios|fxios|edgios|opios|duckduckgo)/.test(normalizedAgent);

  return isIos && isSafari && !isAlternateIosBrowser;
}

// Embedded in-app browsers (the webview inside Gmail, Outlook, Facebook,
// Instagram, etc.) are blocked by Google's OAuth "secure browsers" policy.
// Email magic-link sign-in still works there, so we use this to steer users
// away from the Google button in those contexts. Detection is heuristic and
// kept conservative to avoid hiding Google in real browsers.
const IN_APP_BROWSER_TOKENS = [
  "fban",
  "fbav",
  "fb_iab",
  "fbios",
  "instagram",
  "line/",
  "twitter",
  "snapchat",
  "whatsapp",
  "linkedinapp",
  "micromessenger",
  "gsa/",
  "tiktok",
  "musical_ly",
  "pinterest"
];

export function isInAppBrowser(userAgent: string): boolean {
  const ua = userAgent.toLowerCase();

  if (IN_APP_BROWSER_TOKENS.some((token) => ua.includes(token))) return true;

  // Android System WebView.
  if (ua.includes("; wv)") || ua.includes("; wv;")) return true;

  // iOS embedded WKWebView: an iOS device running WebKit but missing the
  // "safari" token that real Safari and SFSafariViewController always include.
  const isIos = /iphone|ipad|ipod/.test(ua);
  if (isIos && ua.includes("applewebkit") && !ua.includes("safari")) return true;

  return false;
}

export function isStandalonePwa(): boolean {
  const navigatorWithStandalone = window.navigator as StandaloneNavigator;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    navigatorWithStandalone.standalone === true
  );
}

export function hasDismissedHomeScreenPrompt(): boolean {
  try {
    return window.localStorage.getItem(HOME_SCREEN_PROMPT_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissHomeScreenPrompt() {
  try {
    window.localStorage.setItem(HOME_SCREEN_PROMPT_DISMISSED_KEY, "1");
  } catch {
    // The prompt still closes for the current session if storage is unavailable.
  }
}

export function currentHomeScreenPromptEnvironment(): HomeScreenPromptEnvironment {
  return {
    userAgent: window.navigator.userAgent,
    platform: window.navigator.platform,
    standalone: isStandalonePwa(),
    dismissed: hasDismissedHomeScreenPrompt()
  };
}

export function shouldShowHomeScreenPrompt(environment: HomeScreenPromptEnvironment): boolean {
  return (
    !environment.standalone &&
    !environment.dismissed &&
    isIosSafari(environment.userAgent, environment.platform)
  );
}
